import { getTopObstructionHeight } from '~/composables/useTopObstructionHeight'
import { getTargetScrollTop } from '~/utils/scroll'

export function useScrollToElement(containerKey: ContainerKey | Readonly<Ref<HTMLDivElement | null>>) {
  const containers = inject(diKeys.containers)

  return {
    scrollToElement(target: string | Element, offset: boolean = false, onComplete?: () => void) {
      // Use requestAnimationFrame for smoother execution aligned with browser rendering
      requestAnimationFrame(() => {
        const targetElement = typeof target === 'string' ? document.getElementById(target) : target
        const container = typeof containerKey === 'string' ? toValue(containers?.[containerKey]) : toValue(containerKey)

        if (!targetElement || !container) {
          console.warn(`Target element or container not found for targetId: ${toValue(target)}, containerId: ${containerKey}`)
          return
        }

        // Get element and container positions
        const rect = targetElement.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const topObstructionHeight = getTopObstructionHeight(container, containerRect)
        const margin = 10 // Small padding below sticky UI for readability
        const elementHeight = rect.height

        // Skip scroll if element is already visible in the viewport
        const visibleTop = topObstructionHeight + margin
        const visibleBottom = containerRect.height
        const elementTopInContainer = rect.top - containerRect.top

        if (elementTopInContainer > visibleTop && elementTopInContainer + elementHeight < visibleBottom) {
          onComplete?.()
          return
        }

        const extraOffset = offset ? Math.min(elementHeight, 100) : 0
        const finalScrollTop = getTargetScrollTop(targetElement, container, { extraOffset })

        // Honour prefers-reduced-motion to avoid vestibular-disorder triggers.
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (onComplete) {
          // Wait for scrolling to finish before triggering the callback so focus and screen reader announcements match the visible state.
          // Fallback to setTimeout for browsers without `scrollend` support or when reduced motion is enabled.
          const fallback = setTimeout(onComplete, prefersReducedMotion ? 0 : 450)
          container.addEventListener('scrollend', () => {
            clearTimeout(fallback)
            onComplete()
          }, { once: true })
        }

        container.scrollTo({
          top: finalScrollTop,
          behavior: prefersReducedMotion ? 'instant' : 'smooth',
        })
      })
    },
  }
}
